import { Row, Popover, Tag } from 'antd';
import { ActiveFilterEnum } from '../../enums/ActiveFilterEnum';
import { ThreeDotsVertical, Pencil, Trash, ArrowCounterclockwise } from 'react-bootstrap-icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import TableComponent from '../common/TableComponent';
import { ActionMenu } from '../common/ActionMenu';
import { IMainProductRecord, MainProduct } from '../../types/mainProduct';
import { ProductCategory } from '../../types/productCategory';
import { useIsOnlineContext } from '../../contexts/IsOnlineContext';

interface MainProductListFormProps {
  products: MainProduct[];
  handleEdit?: (data: IMainProductRecord) => void;
  handleDelete?: (id: string) => void;
  getDeleteConfirmation?: (id: string) => void;
  getRestoreConfirmation?: (id: string) => void;
  totalProducts: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, size?: number) => void;
  loading?: boolean;
  statusFilter?: ActiveFilterEnum;
}

const MainProductList: React.FC<MainProductListFormProps> = ({
  products,
  handleEdit,
  getDeleteConfirmation,
  getRestoreConfirmation,
  totalProducts,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  loading = false,
  statusFilter = ActiveFilterEnum.ACTIVE,
}) => {
  const { t } = useTranslation(['mainProduct', 'form', 'common']);
  const { isOnline } = useIsOnlineContext();

  const getProductColumns = () => [
    {
      title: t('labels.productName'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: t('labels.noOfPresentations'),
      dataIndex: 'presentations',
      key: 'presentation',
      width: 140,
      align: 'center' as const,
      render: (presentations: string[]) => {
        if (!presentations || presentations?.length === 0) {
          return <span className="text-gray-600">N/A</span>;
        }
        return (
          <Popover
            content={
              <div className="max-h-[150px] max-w-xs space-y-1 overflow-auto p-1">
                {presentations?.map((presentation: any, index) => (
                  <div key={index} className="text-sm text-gray-700">
                    • {presentation?.name}
                  </div>
                ))}
              </div>
            }
            title={t('labels.presentations')}
            trigger="click"
            placement="top"
          >
            <Tag className="cursor-pointer transition-colors hover:bg-blue-100" color="blue">
              {presentations?.length}
            </Tag>
          </Popover>
        );
      },
    },
    {
      title: t('labels.noOfBrands'),
      dataIndex: 'brands',
      key: 'brand',
      width: 140,
      align: 'center' as const,
      render: (brands: string[]) => {
        if (!brands || brands?.length === 0) {
          return <span className="text-gray-600">N/A</span>;
        }

        return (
          <Popover
            content={
              <div className="max-h-[150px] max-w-xs space-y-1 overflow-auto p-1">
                {brands?.map((brand: any, index) => (
                  <div key={index} className="text-sm text-gray-700">
                    • {brand?.name}
                  </div>
                ))}
              </div>
            }
            title={t('labels.brands')}
            trigger="click"
            placement="top"
          >
            <Tag className="cursor-pointer transition-colors hover:bg-blue-100" color="blue">
              {brands?.length}
            </Tag>
          </Popover>
        );
      },
    },
    {
      title: t('labels.unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
      align: 'center' as const,
      render: (unit: string) => (
        <span className="text-gray-600">{unit ? t(`form:measuringUnits.${unit}`) : 'N/A'}</span>
      ),
    },
    {
      title: t('labels.productCategory'),
      dataIndex: 'productCategory',
      key: 'category',
      width: 150,
      align: 'center' as const,
      render: (category: ProductCategory | null) => (
        <span className="text-gray-600">
          {category && category?.category
            ? `${category?.mainCategory} > ${category?.category}`
            : category && category?.mainCategory
              ? `${category?.mainCategory}`
              : 'N/A'}
        </span>
      ),
    },
    {
      title: t('created'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 120,
      align: 'center' as const,
      render: (createTime: string) => (
        <span className="text-sm text-gray-600">
          {dayjs(Number(createTime)).format('YYYY-MM-DD')}
        </span>
      ),
    },
    {
      title: t('labels.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 110,
      align: 'center' as const,
      render: (isActive: boolean) => (
        <Tag
          color={isActive ? 'green' : 'red'}
          className="px-3 py-0.5 text-xs font-medium"
        >
          {isActive ? t('common:statusFilter.active') : t('common:statusFilter.inactive')}
        </Tag>
      ),
    },
    {
      title: t('action'),
      dataIndex: 'action',
      key: 'action',
      width: 100,
      align: 'center' as const,
      render: (_: unknown, record: IMainProductRecord) => {
        const menuItems: any =
          statusFilter === ActiveFilterEnum.INACTIVE || record?.isActive === false
            ? [
                {
                  text: t('restore'),
                  icon: <ArrowCounterclockwise size={16} className="text-primary-blue-icon" />,
                  click: () => getRestoreConfirmation?.(record?.id),
                  disabled: !isOnline,
                  description: !isOnline && t('offlineRestoreInfoTitle'),
                },
              ]
            : [
                {
                  text: t('edit'),
                  icon: <Pencil size={16} className="text-primary-blue-icon" />,
                  click: () => handleEdit?.(record),
                  disabled: isOnline ? false : true,
                  description: !isOnline && t('offlineEditInfoTitle'),
                },
                {
                  text: t('delete'),
                  icon: <Trash size={16} className="text-primary-blue-icon" />,
                  click: () => getDeleteConfirmation?.(record?.id),
                  disabled: isOnline ? false : true,
                  description: !isOnline && t('offlineDeleteInfoTitle'),
                },
              ];

        return (
          <Popover
            content={<ActionMenu menuItems={menuItems} />}
            className="cursor-pointer"
            placement="bottom"
            trigger="hover"
            overlayStyle={{ zIndex: 800 }}
          >
            <Row justify="center">
              <ThreeDotsVertical size={15} />
            </Row>
          </Popover>
        );
      },
    },
  ];

  return (
    <>
      <div className="mt-6 w-full overflow-x-auto">
        <TableComponent
          tableData={products}
          pageSize={pageSize}
          currentPage={currentPage}
          totalItems={totalProducts}
          onPaginationChanged={onPageChange}
          columns={getProductColumns()}
          loading={loading}
          showHeader
        />
      </div>
    </>
  );
};

export default MainProductList;
